import { formatDate } from "../../../utils/formatters";

/** Safe numeric parse for API values. */
export function num(v) {
  if (v == null || v === "") return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function formatCompact(n, maxFrac = 1) {
  if (!Number.isFinite(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(maxFrac)}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(maxFrac)}k`;
  return formatFixed(n, maxFrac > 0 ? 1 : 0);
}

function formatFixed(n, decimals = 2) {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  }).format(n);
}

/** API may use different casings or Date objects for date columns. */
export function normalizePeriodKey(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const d = new Date(value);
    return Number.isNaN(d.getTime())
      ? String(value)
      : d.toISOString().slice(0, 10);
  }
  const s = String(value).trim();
  return s || null;
}

function pickPeriodField(row, granularity) {
  if (granularity === "weekly") {
    const v =
      row.week_ending_date ?? row.Week_Ending_Date ?? row.WEEK_ENDING_DATE;
    if (v != null && v !== "") return v;
    return row.cohort_date ?? row.Cohort_Date ?? null;
  }
  const v = row.calendar_date ?? row.Calendar_Date ?? row.CALENDAR_DATE;
  if (v != null && v !== "") return v;
  return row.cohort_date ?? row.Cohort_Date ?? null;
}

/**
 * Aggregate rows by period (week ending or calendar day) for chart series.
 * Multiple cohort dates in the same period are combined with volume-weighted RPH.
 */
export function aggregateByPeriod(rows, granularity) {
  const map = new Map();

  for (const r of rows) {
    const raw = pickPeriodField(r, granularity);
    const p = normalizePeriodKey(raw);
    if (p == null) continue;
    if (!map.has(p)) {
      map.set(p, {
        hours: 0,
        units: 0,
        coverageSum: 0,
        coverageCount: 0,
      });
    }
    const a = map.get(p);
    a.hours += num(r.cohort_total_hours);
    a.units += num(r.cohort_total_units);
    const cs = num(r.cohort_size);
    const cw = num(r.contributors_with_productivity);
    if (cs > 0) {
      a.coverageSum += cw / cs;
      a.coverageCount += 1;
    }
  }

  return [...map.entries()]
    .sort(([a], [b]) => String(a).localeCompare(String(b)))
    .map(([period, a]) => ({
      period,
      periodLabel: shortPeriodLabel(period),
      totalHours: a.hours,
      totalUnits: a.units,
      weightedRph: a.hours > 0 ? a.units / a.hours : 0,
      avgCoveragePct:
        a.coverageCount > 0 ? (a.coverageSum / a.coverageCount) * 100 : 0,
    }));
}

function shortPeriodLabel(iso) {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  } catch {
    return String(iso);
  }
}

/**
 * Summary insights derived only from row data (no extra API calls).
 */
export function computeCohortInsights(rows, granularity) {
  if (!rows?.length) {
    return {
      rowCount: 0,
      totalHours: 0,
      totalUnits: 0,
      avgWeightedRph: 0,
      avgProductivityCoveragePct: 0,
      peakRph: null,
      coverageRange: null,
      trendLabel: null,
      trendDeltaRph: null,
    };
  }

  let totalHours = 0;
  let totalUnits = 0;
  let coverageNum = 0;
  let coverageDen = 0;
  let peakRph = { rph: -1, label: null };

  for (const r of rows) {
    const h = num(r.cohort_total_hours);
    const u = num(r.cohort_total_units);
    totalHours += h;
    totalUnits += u;
    const cs = num(r.cohort_size);
    const cw = num(r.contributors_with_productivity);
    if (cs > 0) {
      coverageNum += cw / cs;
      coverageDen += 1;
    }
    const rph = num(r.cohort_weighted_avg_rph);
    if (rph > peakRph.rph) {
      const raw = pickPeriodField(r, granularity);
      const when = raw != null ? formatDate(raw) : formatDate(r.cohort_date);
      peakRph = {
        rph,
        label: when,
      };
    }
  }

  const avgWeightedRph = totalHours > 0 ? totalUnits / totalHours : 0;
  const avgProductivityCoveragePct =
    coverageDen > 0 ? (coverageNum / coverageDen) * 100 : 0;

  let minCov = Infinity;
  let maxCov = -Infinity;
  for (const r of rows) {
    const cs = num(r.cohort_size);
    if (cs <= 0) continue;
    const c = (num(r.contributors_with_productivity) / cs) * 100;
    minCov = Math.min(minCov, c);
    maxCov = Math.max(maxCov, c);
  }
  if (!Number.isFinite(minCov)) minCov = 0;
  if (!Number.isFinite(maxCov)) maxCov = 0;

  const series = aggregateByPeriod(rows, granularity);
  const trend = computeTrendDelta(series);

  return {
    rowCount: rows.length,
    totalHours,
    totalUnits,
    avgWeightedRph,
    avgProductivityCoveragePct,
    peakRph: peakRph.rph >= 0 ? peakRph : null,
    coverageRange: rows.length > 0 ? { min: minCov, max: maxCov } : null,
    trendLabel: trend.label,
    trendDeltaRph: trend.delta,
  };
}

function computeTrendDelta(series) {
  if (series.length < 4) {
    return { label: "Not enough periods to trend", delta: null };
  }
  const mid = Math.floor(series.length / 2);
  const first = series.slice(0, mid);
  const second = series.slice(mid);
  const avg = (arr) =>
    arr.length ? arr.reduce((s, x) => s + x.weightedRph, 0) / arr.length : 0;
  const a1 = avg(first);
  const a2 = avg(second);
  const delta = a2 - a1;
  const thresh = 0.01;
  let label = "RPH stable vs mid-range";
  if (delta > thresh) label = "RPH trending up in later periods";
  else if (delta < -thresh) label = "RPH trending down in later periods";
  return { label, delta };
}

export function productivityCoveragePct(row) {
  const cs = num(row.cohort_size);
  if (cs <= 0) return null;
  return (num(row.contributors_with_productivity) / cs) * 100;
}
